import { formatUsd, NANO_PER_USD } from '@console/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CreditCard, ExternalLink } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Badge, ErrorText, Field, Notice, Spinner, Toggle } from '../../components/ui/misc'
import { useToast } from '../../components/ui/toast'
import { api, useBilling, useConfig, type Billing } from '../../lib/api'
import { fmtDateTime, LEDGER_LABEL } from '../../lib/format'
import { Section } from './SettingsLayout'

const PRESETS = [10, 25, 50, 100]
const usd = (nano: number) => String(nano / NANO_PER_USD)

export function BillingPage() {
  const { data: b, isLoading } = useBilling()
  const qc = useQueryClient()
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const [addOpen, setAddOpen] = useState(false)

  useEffect(() => {
    const t = params.get('topup')
    if (!t) return
    if (t === 'success') {
      toast('Payment received. Credits appear within a few seconds.')
      const timer = setInterval(() => qc.invalidateQueries({ queryKey: ['billing'] }), 2000)
      setTimeout(() => clearInterval(timer), 12_000)
    } else toast('Checkout cancelled', 'error')
    params.delete('topup')
    setParams(params, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading || !b) return <Spinner />

  const portal = async () => {
    try {
      const { url } = await api<{ url: string }>('/api/billing/portal', { body: {} })
      window.location.href = url
    } catch (e) {
      toast((e as Error).message, 'error')
    }
  }

  return (
    <>
      {b.status === 'suspended' && (
        <div className="mb-5">
          <Notice tone="danger" title="This organization is suspended">
            API calls are refused. Contact support.
          </Notice>
        </div>
      )}
      <Section
        title="Balance"
        actions={
          b.canManage && (
            <>
              {b.billingEnabled && (
                <Button variant="secondary" onClick={portal}>
                  <ExternalLink className="h-3.5 w-3.5" /> Payment methods & invoices
                </Button>
              )}
              <Button onClick={() => setAddOpen(true)} disabled={!b.billingEnabled}>
                <CreditCard className="h-3.5 w-3.5" /> Add credits
              </Button>
            </>
          )
        }
      >
        <div className={`text-[32px] font-semibold tabular-nums tracking-[-0.02em] ${b.balanceNano <= 0 ? 'text-danger' : 'text-ink'}`}>{formatUsd(b.balanceNano)}</div>
        {b.balanceNano <= 0 && <div className="text-[12.5px] text-danger">API calls are refused until you add credits.</div>}
        {!b.billingEnabled && b.canManage && <div className="mt-1 text-[12.5px] text-ink-muted">Card payments open soon.</div>}
      </Section>

      {b.canManage && b.billingEnabled && <AutoTopup b={b} />}
      <Budget />
      {b.canManage && <Redeem />}

      <Section title="History">
        {b.ledger.length === 0 ? (
          <p className="text-[12.5px] text-ink-muted">Nothing yet.</p>
        ) : (
          <div className="-mx-3 overflow-x-auto">
            <table className="w-full min-w-[520px]">
              <thead className="border-b border-line">
                <tr>
                  <th className="th">Date</th>
                  <th className="th">Type</th>
                  <th className="th">Note</th>
                  <th className="th text-right">Amount</th>
                  <th className="th text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {b.ledger.map((l) => (
                  <tr key={l.id} className="border-b border-line-subtle last:border-0">
                    <td className="td whitespace-nowrap text-ink-secondary">{fmtDateTime(l.createdAt)}</td>
                    <td className="td">
                      <Badge tone={l.amountNano >= 0 ? 'success' : 'neutral'}>{LEDGER_LABEL[l.kind] ?? l.kind}</Badge>
                    </td>
                    <td className="td text-ink-secondary">{l.note}</td>
                    <td className={`td text-right tabular-nums ${l.amountNano < 0 ? 'text-danger' : ''}`}>
                      {l.amountNano >= 0 ? '+' : ''}
                      {formatUsd(l.amountNano)}
                    </td>
                    <td className="td text-right tabular-nums text-ink-secondary">{formatUsd(l.balanceAfterNano)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <AddCredits open={addOpen} onClose={() => setAddOpen(false)} />
    </>
  )
}

/** On/off on the heading; the amounts only while on; Save only once something changed. */
function AutoTopup({ b }: { b: Billing }) {
  const qc = useQueryClient()
  const toast = useToast()
  const saved = {
    enabled: b.autoTopup.enabled,
    thresholdUsd: usd(b.autoTopup.thresholdNano || 5 * NANO_PER_USD),
    amountUsd: usd(b.autoTopup.amountNano || 25 * NANO_PER_USD),
  }
  const [f, setF] = useState(saved)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<unknown>(null)
  const savedKey = JSON.stringify(saved)
  useEffect(() => setF(JSON.parse(savedKey)), [savedKey])
  const dirty = JSON.stringify(f) !== savedKey
  const canUse = b.autoTopup.hasPaymentMethod

  const save = async () => {
    setBusy(true)
    setErr(null)
    try {
      await api('/api/billing/auto-topup', { method: 'PUT', body: { enabled: f.enabled, thresholdUsd: Number(f.thresholdUsd), amountUsd: Number(f.amountUsd) } })
      toast(f.enabled ? 'Auto top-up on' : 'Auto top-up off')
      await qc.invalidateQueries({ queryKey: ['billing'] })
    } catch (e) {
      setErr(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Section
      title="Auto top-up"
      actions={<Toggle checked={f.enabled} disabled={!canUse} onChange={(v) => setF({ ...f, enabled: v })} />}
      footer={
        dirty && (
          <>
            <Button variant="ghost" onClick={() => setF(saved)}>
              Cancel
            </Button>
            <Button onClick={save} loading={busy}>
              Save
            </Button>
          </>
        )
      }
    >
      {!canUse ? (
        <p className="text-[12.5px] text-ink-muted">Pay by card once to turn this on.</p>
      ) : f.enabled ? (
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-secondary">
          When the balance falls below
          <UsdInput value={f.thresholdUsd} onChange={(v) => setF({ ...f, thresholdUsd: v })} />
          add
          <UsdInput value={f.amountUsd} onChange={(v) => setF({ ...f, amountUsd: v })} />
        </div>
      ) : null}
      {err != null && (
        <div className="mt-2">
          <ErrorText error={err} />
        </div>
      )}
    </Section>
  )
}

function UsdInput({ value, onChange, placeholder, disabled }: { value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-ink-muted">$</span>
      <input
        className="input !w-28 !pl-6 tabular-nums"
        type="number"
        min={0}
        step="0.01"
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

interface BudgetsResp {
  monthlyNano: number | null
  alertPct: number
  spend: { monthNano: number }
  resetsAt: string
  canManage: boolean
}

/** The organization's monthly cap: a switch on the heading; the cap and alert level only while it's on. */
function Budget() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data: b } = useQuery({ queryKey: ['budgets'], queryFn: () => api<BudgetsResp>('/api/budgets') })
  const saved = b ? { enabled: b.monthlyNano != null, cap: b.monthlyNano == null ? '' : usd(b.monthlyNano), alertPct: String(b.alertPct) } : null
  const savedKey = JSON.stringify(saved)
  const [f, setF] = useState({ enabled: false, cap: '', alertPct: '80' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<unknown>(null)
  useEffect(() => {
    if (savedKey !== 'null') setF(JSON.parse(savedKey))
  }, [savedKey])
  if (!b) return null
  const dirty = JSON.stringify(f) !== savedKey
  const valid = !f.enabled || Number(f.cap) > 0

  const save = async () => {
    setBusy(true)
    setErr(null)
    try {
      await api('/api/budgets', { method: 'PUT', body: { monthlyUsd: f.enabled ? Number(f.cap) : null, alertPct: Number(f.alertPct) } })
      toast(f.enabled ? 'Budget saved' : 'Budget off')
      await qc.invalidateQueries({ queryKey: ['budgets'] })
    } catch (e) {
      setErr(e)
    } finally {
      setBusy(false)
    }
  }

  const spent = b.spend.monthNano
  const cap = b.monthlyNano
  const used = cap ? Math.min(1, spent / cap) : 0
  return (
    <Section
      title="Monthly budget"
      actions={<Toggle checked={f.enabled} disabled={!b.canManage} onChange={(v) => setF({ ...f, enabled: v })} />}
      footer={
        dirty &&
        b.canManage && (
          <>
            <Button variant="ghost" onClick={() => setF(JSON.parse(savedKey))}>
              Cancel
            </Button>
            <Button onClick={save} loading={busy} disabled={!valid}>
              Save
            </Button>
          </>
        )
      }
    >
      {f.enabled && (
        <div className="space-y-4">
          {cap != null && (
            <div className="flex items-center gap-3 text-[13px]" title={`Resets ${fmtDateTime(b.resetsAt)}`}>
              <span className="tabular-nums text-ink">
                {formatUsd(spent)} <span className="text-ink-muted">of {formatUsd(cap)} this month</span>
              </span>
              <div className="h-1.5 w-40 overflow-hidden rounded-full bg-surface-sunken">
                <div
                  className={`h-full rounded-full ${spent >= cap ? 'bg-danger' : used * 100 >= b.alertPct ? 'bg-warning' : 'bg-brand'}`}
                  style={{ width: `${Math.max(used * 100, spent > 0 ? 2 : 0)}%` }}
                />
              </div>
            </div>
          )}
          {b.canManage && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px] text-ink-secondary">
              <label className="flex items-center gap-2">
                Cap
                <UsdInput value={f.cap} onChange={(v) => setF({ ...f, cap: v })} />
                per month
              </label>
              <label className="flex items-center gap-2">
                Email an alert at
                <input className="input !w-16 tabular-nums" type="number" min={10} max={99} value={f.alertPct} onChange={(e) => setF({ ...f, alertPct: e.target.value })} />%
              </label>
            </div>
          )}
        </div>
      )}
      {err != null && (
        <div className="mt-2">
          <ErrorText error={err} />
        </div>
      )}
    </Section>
  )
}

function Redeem() {
  const qc = useQueryClient()
  const toast = useToast()
  const [code, setCode] = useState('')
  const [err, setErr] = useState<unknown>(null)
  const redeem = async () => {
    setErr(null)
    try {
      const r = await api<{ amountNano: number }>('/api/billing/redeem', { body: { code } })
      toast(`${formatUsd(r.amountNano)} added`)
      setCode('')
      void qc.invalidateQueries({ queryKey: ['billing'] })
    } catch (e) {
      setErr(e)
    }
  }
  return (
    <Section title="Redeem a code">
      <div className="flex max-w-sm gap-2">
        <input className="input font-mono uppercase" placeholder="CODE" value={code} onChange={(e) => setCode(e.target.value)} />
        <Button variant="secondary" onClick={redeem} disabled={!code.trim()}>
          Redeem
        </Button>
      </div>
      {err != null && (
        <div className="mt-2">
          <ErrorText error={err} />
        </div>
      )}
    </Section>
  )
}

function AddCredits({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: cfg } = useConfig()
  const [amount, setAmount] = useState('25')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<unknown>(null)
  const min = cfg?.topup.minUsd ?? 5
  const max = cfg?.topup.maxUsd ?? 5000
  const checkout = async () => {
    setBusy(true)
    setErr(null)
    try {
      const { url } = await api<{ url: string }>('/api/billing/checkout', { body: { amountUsd: Number(amount) } })
      window.location.href = url
    } catch (e) {
      setErr(e)
      setBusy(false)
    }
  }
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add credits"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={checkout} loading={busy} disabled={!(Number(amount) >= min && Number(amount) <= max)}>
            Continue to payment
          </Button>
        </>
      }
    >
      <div className="mb-3 grid grid-cols-4 gap-2">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setAmount(String(p))}
            className={`rounded-md border py-2 text-[13px] font-medium ${Number(amount) === p ? 'border-brand bg-brand-soft text-brand' : 'border-line text-ink hover:bg-surface-hover'}`}
          >
            ${p}
          </button>
        ))}
      </div>
      <Field label="Amount (USD)" hint={`$${min} to $${max.toLocaleString()}`}>
        <input className="input" type="number" min={min} max={max} value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <div className="mt-3">
        <ErrorText error={err} />
      </div>
    </Modal>
  )
}
